import React from "react";
import styled from "styled-components";
import { FiChevronRight, FiFileText } from "react-icons/fi";
import { colors } from "./colors";
import { changeOf, displayEntity, isUrlEntity, lostOf } from "./drops";
import { daysBetween, formatCompact, formatDay, formatSignedPercent } from "./format";
import SeverityBadge from "./SeverityBadge";
import CauseTag from "./CauseTag";
import Sparkline from "./Sparkline";

export const MONO = `"JetBrains Mono", ui-monospace, SFMono-Regular, monospace`;

export default function Row({ drop, measure, today, selected, onSelect }) {
    const figures = drop.byMeasure[measure];
    const daysAgo = daysBetween(drop.detectedAt, today);
    const firstChange = drop.pageChanges[0];
    return (
        <Tr tabIndex={0} aria-selected={selected} $selected={selected} onClick={() => onSelect(drop.id)}
            onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onSelect(drop.id);
                }
            }}>
            <Td $first><SeverityBadge severity={drop.severity} /></Td>
            <Td>
                <Line>
                    <Entity $url={isUrlEntity(drop)}>{displayEntity(drop)}</Entity>
                    {firstChange && (
                        <ChangeChip title={drop.pageChanges.map((change) => `${change.field} changed ${formatDay(change.date)}`).join("\n")}>
                            <FiFileText aria-hidden />
                            {firstChange.field} changed {formatDay(firstChange.date)}
                        </ChangeChip>
                    )}
                </Line>
                <Summary><Faint>{drop.entityType} · </Faint>{drop.summary}</Summary>
            </Td>
            <Td $from={1100}><CauseTag cause={drop.cause} /></Td>
            <Td $from={1100} $right>
                <Muted>{formatCompact(figures.before)}</Muted>
                <Arrow>→</Arrow>
                <Strong>{formatCompact(figures.after)}</Strong>
            </Td>
            <Td $right>
                <Change>{formatSignedPercent(changeOf(drop, measure))}</Change>
                <Small>−{formatCompact(lostOf(drop, measure))}/wk</Small>
            </Td>
            <Td $from={900}><Sparkline values={figures.trend} breakIndex={drop.trendBreak} /></Td>
            <Td $from={1280}>
                <Detected>{formatDay(drop.detectedAt)}</Detected>
                <Small>{daysAgo === 0 ? "Today" : `${daysAgo}d ago`}</Small>
            </Td>
            <Td $right $last><Chevron aria-hidden /></Td>
        </Tr>
    );
}

export const hiddenBelow = (width) => width && `@media (max-width: ${width - 1}px) { display: none; }`;

const Chevron = styled(FiChevronRight)`
    display: block;
    margin-left: auto;
    color: ${colors.subtle};
    transition: transform 150ms, color 150ms;
`;

const Tr = styled.tr`
    cursor: pointer;
    vertical-align: middle;
    border-top: 1px solid ${colors.line};
    background: ${({ $selected }) => ($selected ? colors.accentSoft : "transparent")};
    transition: background 150ms;
    &:hover { background: ${({ $selected }) => ($selected ? colors.accentSoft : colors.canvas)}; }
    &:hover ${Chevron} { transform: translateX(2px); color: ${colors.ink}; }
    &:focus-visible { outline: none; background: ${colors.accentSoft}; }
`;

const Td = styled.td`
    padding: 14px 12px 14px 0;
    text-align: ${({ $right }) => ($right ? "right" : "left")};
    font-variant-numeric: tabular-nums;
    ${({ $first }) => $first && "padding-left: 20px;"}
    ${({ $last }) => $last && "padding-right: 16px;"}
    ${({ $from }) => hiddenBelow($from)}
`;

const Line = styled.div`
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 8px;
`;

const Entity = styled.p`
    flex: 1 1 auto;
    min-width: 0;
    margin: 0;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    font-weight: 500;
    font-family: ${({ $url }) => ($url ? MONO : "inherit")};
    font-size: ${({ $url }) => ($url ? 13 : 14)}px;
    color: ${colors.ink};
`;

const ChangeChip = styled.span`
    display: inline-flex;
    flex-shrink: 1;
    min-width: 0;
    max-width: 45%;
    overflow: hidden;
    text-overflow: ellipsis;
    align-items: center;
    gap: 4px;
    white-space: nowrap;
    border-radius: 4px;
    padding: 2px 6px;
    font-size: 11px;
    font-weight: 500;
    background: ${colors.pageChange}14;
    color: ${colors.pageChange};
`;

const Summary = styled.p`
    margin: 2px 0 0;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    font-size: 12px;
    color: ${colors.muted};
`;

const Faint = styled.span`
    color: ${colors.subtle};
`;

const Muted = styled.span`
    font-size: 13px;
    color: ${colors.muted};
`;

const Arrow = styled.span`
    padding: 0 4px;
    color: ${colors.subtle};
`;

const Strong = styled.span`
    font-size: 13px;
    font-weight: 500;
    color: ${colors.ink};
`;

const Change = styled.p`
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: ${colors.criticalInk};
`;

const Small = styled.p`
    margin: 0;
    font-size: 12px;
    color: ${colors.muted};
`;

const Detected = styled.p`
    margin: 0;
    white-space: nowrap;
    font-size: 13px;
    color: ${colors.ink};
`;
